const en = {
  nav: {
    studio: "Studio",
    servizi: "Services",
    portfolio: "Portfolio",
    team: "Team",
    journal: "Journal",
    studioTagline: "Digital studio",
    contatti: "Contact",
    parliamone: "Let's talk",
  },
  common: {
    iniziamoProgetto: "Start a project",
    scriviStudio: "Write to the studio",
    vediServizi: "See all services",
    conosciStudio: "Meet the studio",
    scopriDiPiu: "Learn more",
  },
  footer: {
    overline: "Studio · 2026",
    headline1: "We craft",
    headlineEditorial: "bespoke",
    headline2: "digital experiences.",
    navHeading: "Navigate",
    writeHeading: "Write to us",
    tagline:
      "3D modeling · Prototyping · Digital environments · Creative support.",
    rights: "All rights reserved",
    madeWith: "Made with quiet obsession",
  },
  home: {
    hero: {
      overline: "Digital studio · Founded by Riccardo & Sasha",
      title1: "We give",
      titleEditorial: "shape",
      title2: "to your",
      title3: "imagination.",
      paragraph:
        "Mysterious Stew is a two-person studio that blends 3D modeling, prototyping and digital environments into a single creative workshop — supported by a network of external collaborators for every need.",
      cta1: "Start a project",
      cta2: "What we do",
    },
    marquee: [
      "3D Modeling",
      "Rendering",
      "Prototyping",
      "STL Optimization",
      "Digital Environments",
      "Creative Direction",
      "Print-ready",
      "Motion",
    ],
    services: {
      overline: "Services · Four disciplines",
      title1: "One studio,",
      titleEditorial: "four materials",
      title2: "to compose.",
      paragraph:
        "Every project is treated as a one-off. We pick the right skills — in-house or from our collaborators — to deliver the most precise result.",
      items: [
        {
          title: "3D Modeling",
          desc: "Hard-surface and organic sculpting. Assets ready for rendering, cinema, games or printing.",
        },
        {
          title: "Prototyping & 3D printing",
          desc: "From file to tangible piece. Prototypes, production masters, limited editions.",
        },
        {
          title: "Digital environments",
          desc: "Virtual sets, immersive spaces, photorealistic scenes for storytelling and product.",
        },
        {
          title: "Digital support",
          desc: "Ongoing creative direction: consulting, workflow, content production and coordination.",
        },
      ],
    },
    studio: {
      overline: "The studio · Two founders",
      titleRiccardo: "Riccardo",
      titleAmp: "&",
      titleSasha: "Sasha.",
      titleLine2: "A core team,",
      titleLine3: "an extended network.",
      paragraph:
        "There are two of us, but we don't work alone. We build the right team for each project by activating illustrators, animators, sculptors and developers we've been collaborating with for years.",
      role: "Co-founder",
    },
    finalCta: {
      overline: "Next step",
      title1: "Got an idea?",
      titleEditorial: "Let's cook it together.",
      button: "Write to the studio",
    },
  },
  services: {
    header: {
      overline: "Services",
      title1: "Four disciplines,",
      titleEditorial: "one shared language",
      titleEnd: ".",
      paragraph:
        "We don't ship pre-packaged offerings. Every project is walked through — together — from the first sketch to final delivery, activating the right skills at the right time.",
    },
    items: [
      {
        tag: "3D",
        title: "3D Modeling",
        desc: "Hard-surface and organic sculpting, low-poly and high-poly modelling. Assets ready for photorealistic rendering, cinema, video games or 3D printing.",
        deliverables: [
          "STL / OBJ / FBX models",
          "Retopology & UV",
          "Iterative concept-to-model",
          "Production masters",
        ],
      },
      {
        tag: "PRINT",
        title: "Prototyping & 3D printing",
        desc: "From file to tangible piece. Support preparation, resin/FDM optimisation, functional prototypes and limited runs.",
        deliverables: [
          "Print-ready files",
          "Supports & orientation",
          "Functional prototypes",
          "Limited editions",
        ],
      },
      {
        tag: "ENV",
        title: "Digital environments",
        desc: "Virtual sets, immersive spaces, photorealistic or stylised scenes for storytelling, product showcasing, interactive experiences.",
        deliverables: [
          "Photorealistic scenes",
          "Virtual sets",
          "Real-time previews",
          "Animated sequences",
        ],
      },
      {
        tag: "STUDIO",
        title: "Digital support",
        desc: "Ongoing creative direction: consulting, workflow definition, content production and coordination with external collaborators.",
        deliverables: [
          "Creative direction",
          "Content production",
          "Workflow design",
          "On-demand team",
        ],
      },
    ],
    cta: {
      overline: "A hybrid brief?",
      title1: "The best projects",
      title2: "are born on the",
      titleEditorial: "edge",
      titleEnd: ".",
      button: "Tell us about your project",
    },
  },
  team: {
    intro: {
      overline: "The studio",
      title1: "Two hands",
      titleEditorial: "many voices",
      titleEnd: ".",
      paragraph:
        "The core team is two people. Around us rotates a stable network of external collaborators — illustrators, animators, sculptors, developers — that lets us assemble the right team for every project without ever losing coherence.",
    },
    founders: [
      {
        name: "Riccardo",
        role: "Co-founder · Digital sculpting & creative direction",
        badge: "Co-founder 01",
        bio: "Shapes figures, silhouettes and poses. Leads the studio's visual aesthetics and voices the more tactile, material side of our work.",
        focus: ["Sculpting", "Character design", "Art direction"],
      },
      {
        name: "Sasha",
        role: "Co-founder · Hard-surface & technical workflow",
        badge: "Co-founder 02",
        bio: "Curates the technical side: hard-surface, retopology, printability and the architecture of our end-to-end workflows.",
        focus: ["Hard-surface", "Printability", "Tech pipeline"],
      },
    ],
    crew: {
      overline: "The extended network",
      title1: "A team that",
      titleEditorial: "assembles",
      title2: "around the project.",
      paragraph:
        "We're not an agency with fixed departments. We activate the right profiles case by case — the two of us being the constant that guarantees direction, quality and continuity.",
      items: [
        { role: "Illustrators", count: "02", desc: "Concept and visual development." },
        { role: "Animators", count: "02", desc: "Motion and animated sequences." },
        { role: "Sculptors", count: "03", desc: "Finishes and specialty pieces." },
        { role: "Developers", count: "02", desc: "Backend and web integrations." },
      ],
    },
    manifesto: {
      overline: "Our way",
      quote:
        "\u201CWe'd rather ship a few projects built with obsessive care, than many built in a hurry.\u201D",
      button: "Work with us",
    },
  },
  portfolio: {
    header: {
      overline: "Portfolio · Selection",
      title1: "Projects,",
      titleEditorial: "not products",
      titleEnd: ".",
      paragraph:
        "A selection of the studio's recent collaborations — spanning modelling, digital environments and 3D-print-ready prototypes.",
    },
    projects: [
      {
        id: "obsidian-vault",
        title: "Obsidian Vault",
        tag: "Digital environment",
        desc: "Immersive environment for a luxury product launch: modelling, lighting and post-production.",
      },
      {
        id: "forma-01",
        title: "Forma 01",
        tag: "3D Modeling",
        desc: "Series of abstract digital sculptures for a limited collection.",
      },
      {
        id: "corpo-solido",
        title: "Corpo Solido",
        tag: "Prototyping",
        desc: "Rapid prototyping of a mechanical component. Iteration from file to finished piece.",
      },
      {
        id: "vitrea",
        title: "Vitrea",
        tag: "Digital environment",
        desc: "Virtual set for an editorial campaign of an accessories brand.",
      },
      {
        id: "atelier-site",
        title: "Atelier — editorial site",
        tag: "Website",
        desc: "Custom site for a contemporary art studio. Design, development and CMS.",
      },
      {
        id: "collettivo",
        title: "Collettivo",
        tag: "Creative support",
        desc: "Ongoing consulting for a collective of artists: workflow and content production.",
      },
    ],
    cta: {
      overline: "A similar project?",
      title1: "Every project has",
      title2: "its own",
      titleEditorial: "right question",
      titleEnd: ".",
      button: "Let's start a conversation",
    },
  },
  journal: {
    header: {
      overline: "Journal · Notes from the studio",
      title1: "Notes,",
      titleEditorial: "not tutorials",
      titleEnd: ".",
      paragraph:
        "Thoughts, behind-the-scenes and reflections on the craft. We update the journal when we have something worth writing — never to fill a calendar.",
    },
    tags: [
      "3D",
      "Modeling",
      "Print",
      "Studio Notes",
      "Workflow",
      "Creative direction",
      "Environments",
      "Collaborations",
    ],
    readTimeLabel: "Read",
    readTimeUnit: "min",
    backLabel: "Back to journal",
    moreLabel: "More notes",
    entries: [
      {
        slug: "superficie-e-luce",
        date: "02 · 12 · 2026",
        tag: "Studio note",
        title: "How we think about luxury 3D modelling",
        excerpt:
          "An internal note turned public: why our obsession with surface has much less to do with gloss, and much more with light.",
        readTime: "6",
        intro:
          "This note started as an internal conversation in the studio, in front of a render that didn't convince us. The model was technically flawless, and yet it looked… cheap. That's where a reflection began on what truly makes a digital object feel 'luxury'.",
        sections: [
          {
            h: "Surface is not polish",
            p: "When people talk about high-end modelling, the instinct is to add: more polygons, more reflections, more detail. We've learned to do the opposite. A luxury surface is a controlled surface — continuous curvature, clean transitions, no accidental imperfections. Detail exists only where it's needed, and every bevel has a considered radius.",
          },
          {
            h: "Light is what tells the form",
            p: "You judge a model by moving a light across its surface. If the reflections flow without breaking, the form is resolved; if they shatter, no material will save it. That's why we spend more time on curvature than on textures: the perception of quality is born there, long before the final render.",
          },
          {
            h: "What this changes for our clients",
            p: "It means our quotes contain an invisible line item: the time dedicated to what you don't see at first glance but always perceive. It's the difference between a digital object that 'works' and one that feels real, precious, desirable. It's the reason our clients come back.",
          },
        ],
      },
      {
        slug: "anatomia-prototipo",
        date: "01 · 24 · 2026",
        tag: "Workflow",
        title: "From sketch to print — the anatomy of a prototype",
        excerpt:
          "We revisit a recent project to describe how we blend modelling, retopology and 3D printing when every millimetre counts.",
        readTime: "9",
        intro:
          "A mechanical component, tight tolerances and a client with a real deadline. We retrace the steps of a recent project to show how we work when a file has to become a physical object.",
        sections: [
          {
            h: "The first sketch is already engineering",
            p: "Every prototype starts on paper, but with clear constraints from the very first stroke: real dimensions, stress points, print orientation. Drawing without thinking about production means redrawing later. In our workflow, form and feasibility are born together.",
          },
          {
            h: "Retopology: where millimetres are won",
            p: "The passage from sculpted model to production mesh is the quietest and most critical phase. Guaranteed minimum wall thicknesses, watertight geometry, verified surfaces. Sasha checks every critical zone with cross-sections: when one piece has to fit into another, the margin for error is zero.",
          },
          {
            h: "Printing is a verification, not a finish line",
            p: "The first printed piece is never the final one. It exists to measure, assemble, understand where reality drifts from the file. This project took three iterations: the third snapped into place with a clean click, no play. That sound — a precise click — is the real deliverable.",
          },
        ],
      },
      {
        slug: "manifesto-pacchetti",
        date: "01 · 08 · 2026",
        tag: "Journal",
        title: "A small manifesto against pre-packaged bundles",
        excerpt:
          "Every project is a different problem. Here's why we don't offer standard price lists and how this choice changes how we work with you.",
        readTime: "4",
        intro:
          "We're often asked: 'How much does a 3D model cost? And a digital environment?'. The honest answer is: it depends. This small manifesto explains why we don't have a price list, and why we believe that's an advantage for the people who work with us.",
        sections: [
          {
            h: "Every project is a different question",
            p: "A pre-packaged bundle assumes all problems look alike. Our experience says the opposite: the same request — 'a model for printing' — can take three days or three weeks depending on use, material and level of finish. Pricing before understanding means pricing badly.",
          },
          {
            h: "The quote as the first act of the project",
            p: "For us a quote is not a number: it's the first draft of the project. It contains the phases, the planned iterations, what's included and what we suggest postponing. A client reading our quote already knows how we'll work together, before signing anything.",
          },
          {
            h: "Fewer projects, done better",
            p: "Rejecting the bundle logic forces us to choose: we only take on projects we can give real attention to. It's a commercially contrarian choice, but it's why every piece of work that leaves the studio carries the same care, from the first pixel to the last.",
          },
        ],
      },
      {
        slug: "rete-estesa",
        date: "12 · 22 · 2025",
        tag: "Collaborations",
        title: "The extended network: who's behind every project",
        excerpt:
          "Illustrators, animators, sculptors. The people who orbit the core team and how they get picked for each commission.",
        readTime: "5",
        intro:
          "On the site we write 'a core team of two, an extended network of collaborators'. But what does that mean in practice? Who actually touches your projects, and how do we choose who we work with?",
        sections: [
          {
            h: "Two people as the fixed point",
            p: "Riccardo and Sasha follow every project from start to finish: creative direction, client relationship, quality control. Nothing leaves the studio without passing through our hands. This guarantees coherence — the same standard, the same voice, on every delivery.",
          },
          {
            h: "How we choose our collaborators",
            p: "The extended network isn't a list of freelancers found on demand: they're professionals we've worked with for years — illustrators, animators, sculptors, developers. Each one was chosen after real projects, not interviews. If a name is in our network, it's because they've already proven they can hold our standard.",
          },
          {
            h: "The advantage for the client",
            p: "This model combines the best of two worlds: the care and direct accountability of a small studio, with the production capacity of a larger team. The client always talks to the two of us, but behind every project the exact team that commission requires takes shape.",
          },
        ],
      },
    ],
    cta: {
      overline: "Stay in touch",
      title1: "Just enough notes,",
      titleEditorial: "when they matter",
      titleEnd: ".",
      button: "Write to us for collaborations",
    },
  },
  contact: {
    header: {
      overline: "Contact",
      title1: "Let's",
      title2: "start",
      titleEditorial: "talking",
      titleEnd: ".",
      paragraph:
        "A project in mind, an idea to explore, a question? Write to us — take your time. Riccardo and Sasha reply personally within a few days.",
    },
    form: {
      nameLabel: "Name",
      namePlaceholder: "What's your name?",
      emailLabel: "Email",
      emailPlaceholder: "you@email.com",
      subjectLabel: "What are we building?",
      messageLabel: "Message",
      messagePlaceholder: "Tell us about the project, the idea, the deadline…",
      hint: "We reply personally, usually within 3 business days.",
      submit: "Send message",
      submitting: "Sending…",
      validationError: "Please fill in name, email and message.",
      sendError: "Something went wrong. Please try again shortly.",
      successToast: "Message sent — we'll get back to you soon.",
    },
    services: [
      "3D Modeling",
      "Prototyping & 3D printing",
      "Digital environments",
      "Digital support",
      "Other",
    ],
    wizard: {
      stepWord: "Step",
      ofWord: "of",
      step1Title: "What are we working on?",
      step2Title: "What budget do you have in mind?",
      step2Hint: "An estimate only helps shape the proposal — no commitment.",
      budgetOptions: ["Under €500", "€500 – €1,500", "€1,500 – €5,000", "Over €5,000", "To be defined together"],
      step3Title: "When do you need it?",
      timelineOptions: ["Urgent (within 2 weeks)", "Within 1 month", "1 – 3 months", "Flexible"],
      step4Title: "Tell us about the project",
      step5Title: "Your contact details",
      summaryTitle: "Request summary",
      next: "Next",
      back: "Back",
      selectHint: "Select an option to continue",
    },
    thanks: {
      overline: "Received",
      title1: "Thanks —",
      titleEditorial: "we'll be right back",
      titleEnd: ".",
      paragraph:
        "Your message reached the studio. We'll reply personally within a few business days.",
      again: "Send another message",
    },
    info: {
      emailTitle: "Direct email",
      emailNote:
        "For collaborations, print requests or estimates. We reply personally.",
      whereTitle: "Where we are",
      whereValue: "Italy",
      whereNote: "Remote studio · working with clients across Europe and beyond.",
      timeTitle: "Reply times",
      timeValue: "3 business days",
      timeNote: "We reply to every message, one at a time.",
    },
  },
  seo: {
    home: {
      title: "Mysterious Stew — Digital studio · 3D, print and digital environments",
      description:
        "Riccardo and Sasha's digital studio: 3D modelling, prototyping and 3D printing, digital environments and custom creative support.",
    },
    servizi: {
      title: "Services — 3D modelling, 3D printing and digital environments · Mysterious Stew",
      description:
        "Four disciplines, one language: 3D modelling, prototyping and 3D printing, digital environments and ongoing digital support.",
    },
    team: {
      title: "The studio — Riccardo & Sasha · Mysterious Stew",
      description:
        "Two founders, an extended network of collaborators: illustrators, animators, sculptors and developers assembled around every project.",
    },
    journal: {
      title: "Journal — Notes from the studio · Mysterious Stew",
      description:
        "Notes, behind the scenes and reflections on the craft: 3D modelling, print workflows, creative direction.",
    },
    contatti: {
      title: "Contact — Let's start a project · Mysterious Stew",
      description:
        "Tell us about your project: 3D modelling, prototyping, digital environments or creative support. We reply personally within 3 days.",
    },
  },
  mmf: {
    views: "Views",
    likes: "Likes",
    followers: "Followers",
    models: "Published models",
    visit: "See everything on MyMiniFactory",
    collectionsLabel: "Our factions",
    viewerLabel: "3D Preview",
    viewerTitle: "Touch it",
    viewerTitleEditorial: "for real.",
    viewerHint: "Drag to rotate",
    viewerLoading: "Loading model…",
    featuredLabel: "Featured models",
  },
  language: {
    label: "Language",
  },
};

export default en;
